import { useState, useEffect, useRef } from 'react'
import { supabase } from './lib/supabaseClient'
import MessagingSystem from './components/MessagingSystem'
import WhatsAppButton from './components/WhatsAppButton'
import PortalLayout from './components/PortalLayout'
import { responsiveStyles } from './styles/responsiveStyles'

function LegalPortal({ session, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [companyId, setCompanyId] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [contracts, setContracts] = useState([])
  const [documents, setDocuments] = useState([])
  const [compliance, setCompliance] = useState([])
  const [legalCases, setLegalCases] = useState([])
  const [clients, setClients] = useState([])
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [showMessages, setShowMessages] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [userRole] = useState('legal')
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [uploading, setUploading] = useState(false)
  const [docType, setDocType] = useState('contract')
  const [signatureData, setSignatureData] = useState('')
  const [signingContractId, setSigningContractId] = useState(null)
  const [signingRole, setSigningRole] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [templateVars, setTemplateVars] = useState({})
  const [errorMsg, setErrorMsg] = useState('')
  const [expiringContracts, setExpiringContracts] = useState([])
  const [emailCompose, setEmailCompose] = useState({ to: '', subject: '', body: '', client_id: null })
  const [selectedClient, setSelectedClient] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [stats, setStats] = useState({
    totalContracts: 0,
    signedContracts: 0,
    expiringSoon: 0,
    activeCompliance: 0,
    totalCases: 0,
    totalDocuments: 0,
    openCases: 0
  })
  
  const canvasRef = useRef(null)

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'contracts', label: 'Contracts', icon: '📄' },
    { id: 'documents', label: 'Documents', icon: '📎' },
    { id: 'compliance', label: 'Compliance', icon: '✅' },
    { id: 'cases', label: 'Legal Cases', icon: '⚖️' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '📱' },
    { id: 'emails', label: 'Emails', icon: '📧' }
  ]

  // Contract templates
  const contractTemplates = {
    sales: {
      name: 'Sales Agreement',
      type: 'sales',
      content: `SALES AGREEMENT\n\nThis Sales Agreement ("Agreement") is made on {{date}} between Seller: {{seller_name}} and Buyer: {{buyer_name}} for Property: {{property_address}}. Purchase Price: {{price}} {{currency}}.`
    },
    rental: {
      name: 'Rental/Lease Agreement',
      type: 'rental',
      content: `RESIDENTIAL LEASE AGREEMENT\n\nThis Lease Agreement ("Lease") is made on {{date}} between Landlord: {{landlord_name}} and Tenant: {{tenant_name}} for Property: {{property_address}}. Monthly Rent: {{rent}} {{currency}}.`
    },
    management: {
      name: 'Property Management Agreement',
      type: 'management',
      content: `PROPERTY MANAGEMENT AGREEMENT\n\nThis Agreement is made on {{date}} between Owner: {{owner_name}} and Manager: {{manager_name}} for Property: {{property_address}}. Management Fee: {{management_fee}}% of monthly rent.`
    },
    nda: {
      name: 'Non-Disclosure Agreement',
      type: 'nda',
      content: `NON-DISCLOSURE AGREEMENT\n\nThis NDA is made on {{date}} between Disclosing Party: {{disclosing_party}} and Receiving Party: {{receiving_party}}. Confidential Information shall be protected for {{duration_months}} months.`
    },
    listing: {
      name: 'Listing Agreement',
      type: 'listing',
      content: `EXCLUSIVE LISTING AGREEMENT\n\nThis Agreement is made on {{date}} between Broker: {{broker_name}} and Seller: {{seller_name}} for Property: {{property_address}}. Listing Price: {{list_price}} {{currency}}. Commission: {{commission}}%.`
    }
  }

  // Get company ID
  useEffect(() => {
    const getCompanyId = async () => {
      if (!session?.user?.id) return

      const { data: userData, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .maybeSingle()

      if (error) {
        console.error('Error fetching company_id:', error)
        return
      }

      if (!userData?.company_id) {
        console.log('No company_id found')
        return
      }

      setCompanyId(userData.company_id)
      
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', userData.company_id)
        .maybeSingle()
      
      if (company) {
        setCompanyName(company.name)
      }
    }

    getCompanyId()
  }, [session])

  // Fetch all legal data
  useEffect(() => {
    if (companyId) {
      fetchLegalData()
      fetchUnreadMessages()
    }
  }, [activeTab, companyId])

  const fetchUnreadMessages = async () => {
    if (!companyId) return
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .neq('sender_id', session.user.id)
      .eq('is_read', false)
      .eq('company_id', companyId)
    
    setUnreadCount(count || 0)
  }

  const sendEmail = async () => {
    if (!emailCompose.to || !emailCompose.body) {
      alert('Please fill in recipient and message')
      return
    }

    const { error } = await supabase.from('emails').insert({
      to_email: emailCompose.to,
      subject: emailCompose.subject || 'No Subject',
      body: emailCompose.body,
      from_email: session.user.email,
      direction: 'outbound',
      agent_id: session.user.id,
      client_id: emailCompose.client_id || null,
      company_id: companyId
    })

    if (error) {
      alert('Failed to send: ' + error.message)
    } else {
      alert('Email sent successfully!')
      setEmailCompose({ to: '', subject: '', body: '', client_id: null })
      setShowEmailModal(false)
    }
  }

  // Signature drawing
  useEffect(() => {
    if (showSignatureModal && canvasRef.current) {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.strokeStyle = '#000'
      ctx.lineWidth = 2
      let drawing = false
      
      const startDrawing = (e) => {
        drawing = true
        const rect = canvas.getBoundingClientRect()
        const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0
        const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0
        const x = clientX - rect.left
        const y = clientY - rect.top
        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(x, y)
        ctx.stroke()
      }
      
      const draw = (e) => {
        if (!drawing) return
        const rect = canvas.getBoundingClientRect()
        const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0
        const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0
        const x = clientX - rect.left
        const y = clientY - rect.top
        ctx.lineTo(x, y)
        ctx.stroke()
        ctx.beginPath()
        ctx.moveTo(x, y)
      }
      
      const stopDrawing = () => {
        drawing = false
        setSignatureData(canvas.toDataURL())
        ctx.beginPath()
      }
      
      canvas.addEventListener('mousedown', startDrawing)
      canvas.addEventListener('mousemove', draw)
      canvas.addEventListener('mouseup', stopDrawing)
      canvas.addEventListener('touchstart', startDrawing)
      canvas.addEventListener('touchmove', draw)
      canvas.addEventListener('touchend', stopDrawing)
      
      return () => {
        canvas.removeEventListener('mousedown', startDrawing)
        canvas.removeEventListener('mousemove', draw)
        canvas.removeEventListener('mouseup', stopDrawing)
      }
    }
  }, [showSignatureModal])

  const fetchLegalData = async () => {
    if (!companyId) return
    setLoading(true)
    try {
      // Fetch contracts
      const { data: contractsData } = await supabase
        .from('contracts')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      setContracts(contractsData || [])
      
      // Fetch documents
      const { data: docsData } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      setDocuments(docsData || [])
      
      // Fetch compliance
      const { data: complianceData } = await supabase
        .from('compliance')
        .select('*')
        .eq('company_id', companyId)
      setCompliance(complianceData || [])
      
      // Fetch legal cases
      const { data: casesData } = await supabase
        .from('legal_cases')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      setLegalCases(casesData || [])
      
      // Fetch clients
      const { data: clientsData } = await supabase
        .from('clients')
        .select('*')
        .eq('company_id', companyId)
      setClients(clientsData || [])
      
      // Fetch properties
      const { data: propsData } = await supabase
        .from('properties')
        .select('*')
        .eq('company_id', companyId)
      setProperties(propsData || [])
      
      const signed = contractsData?.filter(c => c.status === 'signed').length || 0
      const expiring = contractsData?.filter(c => {
        if (!c.end_date) return false
        const daysLeft = Math.ceil((new Date(c.end_date) - new Date()) / (1000 * 60 * 60 * 24))
        return daysLeft <= 30 && daysLeft > 0
      }).length || 0
      const openCases = casesData?.filter(c => c.status === 'open' || c.status === 'pending').length || 0
      
      setStats({
        totalContracts: contractsData?.length || 0,
        signedContracts: signed,
        expiringSoon: expiring,
        activeCompliance: complianceData?.filter(c => c.status === 'active').length || 0,
        totalCases: casesData?.length || 0,
        totalDocuments: docsData?.length || 0,
        openCases: openCases
      })
      
      const expiringContractsList = contractsData?.filter(c => {
        if (!c.end_date) return false
        const daysLeft = Math.ceil((new Date(c.end_date) - new Date()) / (1000 * 60 * 60 * 24))
        return daysLeft <= 30 && daysLeft > 0
      }) || []
      setExpiringContracts(expiringContractsList)
      
    } catch (err) {
      console.error('Fetch error:', err)
      setErrorMsg(err.message)
    }
    setLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (activeTab === 'contracts') {
        const contractNumber = 'CTR-' + Date.now() + '-' + Math.floor(Math.random() * 1000)
        const contractData = {
          contract_number: contractNumber,
          title: formData.title,
          type: formData.type,
          client_id: formData.client_id || null,
          property_id: formData.property_id || null,
          value: parseFloat(formData.value) || 0,
          currency: formData.currency || 'USD',
          start_date: formData.start_date,
          end_date: formData.end_date,
          status: formData.status || 'draft',
          notes: formData.notes,
          company_id: companyId
        }
        
        if (editingItem) {
          await supabase.from('contracts').update(contractData).eq('id', editingItem.id)
        } else {
          await supabase.from('contracts').insert(contractData)
        }
      } else if (activeTab === 'compliance') {
        const complianceData = {
          property_id: formData.property_id,
          type: formData.type,
          number: formData.number,
          issue_date: formData.issue_date,
          expiry_date: formData.expiry_date,
          status: formData.status || 'active',
          notes: formData.notes,
          company_id: companyId
        }
        
        if (editingItem) {
          await supabase.from('compliance').update(complianceData).eq('id', editingItem.id)
        } else {
          await supabase.from('compliance').insert(complianceData)
        }
      } else if (activeTab === 'cases') {
        const caseNumber = 'CASE-' + Date.now() + '-' + Math.floor(Math.random() * 1000)
        const caseData = {
          case_number: caseNumber,
          title: formData.title,
          type: formData.type,
          description: formData.description,
          client_id: formData.client_id || null,
          property_id: formData.property_id || null,
          status: formData.status || 'open',
          priority: formData.priority || 'medium',
          legal_fees: parseFloat(formData.legal_fees) || 0,
          currency: formData.currency || 'USD',
          filed_date: formData.filed_date,
          opposing_party: formData.opposing_party,
          notes: formData.notes,
          company_id: companyId
        }
        
        if (editingItem) {
          await supabase.from('legal_cases').update(caseData).eq('id', editingItem.id)
        } else {
          await supabase.from('legal_cases').insert(caseData)
        }
      }

      setShowModal(false)
      setEditingItem(null)
      setFormData({})
      fetchLegalData()
    } catch (err) {
      alert('Error: ' + err.message)
    }
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (confirm('Are you sure?')) {
      let table = ''
      if (activeTab === 'contracts') table = 'contracts'
      else if (activeTab === 'compliance') table = 'compliance'
      else if (activeTab === 'cases') table = 'legal_cases'
      
      await supabase.from(table).delete().eq('id', id)
      fetchLegalData()
    }
  }

  const handleDocumentUpload = async (file, type) => {
    if (!file) return
    setUploading(true)
    const fileExt = file.name.split('.').pop()
    const fileName = `${companyId}_legal_${type}_${Date.now()}.${fileExt}`
    
    const { error: uploadError } = await supabase.storage
      .from('legal_docs')
      .upload(fileName, file)
      
    if (uploadError) {
      alert('Upload failed: ' + uploadError.message)
      setUploading(false)
      return
    }
    
    const { data: urlData } = supabase.storage
      .from('legal_docs')
      .getPublicUrl(fileName)
      
    await supabase.from('legal_documents').insert({
      title: file.name,
      type: type,
      file_url: urlData.publicUrl,
      file_name: fileName,
      version: 1,
      company_id: companyId
    })
    
    alert('Document uploaded successfully!')
    fetchLegalData()
    setUploading(false)
    setShowDocModal(false)
  }

  const deleteDocument = async (docId) => {
    if (confirm('Delete this document?')) {
      await supabase.from('legal_documents').delete().eq('id', docId)
      fetchLegalData()
    }
  }

  const addSignature = async () => {
    if (!signatureData) {
      alert('Please draw your signature')
      return
    }
    
    const updateField = signingRole === 'client' ? 'signed_by_client' : 'signed_by_agent'
    const contract = contracts.find(c => c.id === signingContractId)
    
    await supabase.from('contracts').update({
      [updateField]: true,
      signed_at: new Date()
    }).eq('id', signingContractId)
    
    const isFullySigned = (signingRole === 'client' && contract?.signed_by_agent) ||
                          (signingRole === 'agent' && contract?.signed_by_client)
    
    if (isFullySigned) {
      await supabase.from('contracts').update({ status: 'signed' }).eq('id', signingContractId)
      alert('Contract fully signed! 🎉')
    } else {
      alert(`${signingRole} signature added!`)
    }
    
    setShowSignatureModal(false)
    fetchLegalData()
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      setSignatureData('')
    }
  }

  const insertFromTemplate = async () => {
    if (!selectedTemplate) return
    
    let content = selectedTemplate.content
    Object.keys(templateVars).forEach(key => {
      content = content.replace(new RegExp(`{{${key}}}`, 'g'), templateVars[key] || '')
    })
    
    const contractNumber = 'CTR-' + Date.now() + '-' + Math.floor(Math.random() * 1000)
    await supabase.from('contracts').insert({
      contract_number: contractNumber,
      title: `${selectedTemplate.name} - ${templateVars.property_address || 'New Contract'}`,
      type: selectedTemplate.type,
      value: parseFloat(templateVars.price) || 0,
      currency: templateVars.currency || 'USD',
      status: 'draft',
      notes: 'Generated from template',
      company_id: companyId
    })
    
    alert('Contract generated from template!')
    setShowTemplateModal(false)
    fetchLegalData()
  }

  const sendExpiryAlert = async (contractId) => {
    const contract = contracts.find(c => c.id === contractId)
    const client = clients.find(c => c.id === contract?.client_id)
    
    if (!client?.email) {
      alert('Client has no email address')
      return
    }
    
    const { error } = await supabase.from('emails').insert({
      to_email: client.email,
      subject: `Contract Expiry Notice: ${contract.title} - ${companyName}`,
      body: `Dear ${client.full_name},\n\nYour contract "${contract.title}" will expire on ${new Date(contract.end_date).toLocaleDateString()}.\n\nPlease contact us for renewal.\n\nThank you,\n${companyName} Legal Team`,
      from_email: session.user.email,
      direction: 'outbound',
      company_id: companyId
    })
    
    if (error) {
      alert('Failed to send: ' + error.message)
    } else {
      alert(`Expiry alert sent to ${client.email}`)
    }
  }

  const renderForm = () => {
    const inputStyle = { width: '100%', padding: 10, margin: '6px 0', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' }
    
    if (activeTab === 'contracts') {
      return (
        <>
          <input placeholder="Contract Title" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
          <select value={formData.type || ''} onChange={e => setFormData({...formData, type: e.target.value})} style={inputStyle}>
            <option value="">Select Type</option>
            <option value="sales">Sales Agreement</option>
            <option value="rental">Rental Agreement</option>
            <option value="management">Property Management</option>
            <option value="lease">Lease Contract</option>
            <option value="nda">NDA</option>
            <option value="listing">Listing Agreement</option>
            <option value="other">Other</option>
          </select>
          <select value={formData.client_id || ''} onChange={e => setFormData({...formData, client_id: e.target.value})} style={inputStyle}>
            <option value="">Select Client ({clients.length} available)</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <select value={formData.property_id || ''} onChange={e => setFormData({...formData, property_id: e.target.value})} style={inputStyle}>
            <option value="">Select Property ({properties.length} available)</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <input placeholder="Contract Value" type="number" value={formData.value || ''} onChange={e => setFormData({...formData, value: parseFloat(e.target.value)})} style={inputStyle} />
          <select value={formData.currency || 'USD'} onChange={e => setFormData({...formData, currency: e.target.value})} style={inputStyle}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="NGN">NGN</option>
          </select>
          <input type="date" placeholder="Start Date" value={formData.start_date || ''} onChange={e => setFormData({...formData, start_date: e.target.value})} style={inputStyle} />
          <input type="date" placeholder="End Date" value={formData.end_date || ''} onChange={e => setFormData({...formData, end_date: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'draft'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="draft">Draft</option>
            <option value="sent">Sent to Client</option>
            <option value="signed">Signed</option>
            <option value="expired">Expired</option>
          </select>
          <textarea placeholder="Notes" rows="3" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
        </>
      )
    }
    
    if (activeTab === 'compliance') {
      return (
        <>
          <select value={formData.property_id || ''} onChange={e => setFormData({...formData, property_id: e.target.value})} style={inputStyle}>
            <option value="">Select Property</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <select value={formData.type || ''} onChange={e => setFormData({...formData, type: e.target.value})} style={inputStyle}>
            <option value="">Select Type</option>
            <option value="inspection">Inspection</option>
            <option value="permit">Permit</option>
            <option value="license">License</option>
            <option value="insurance">Insurance</option>
            <option value="tax">Tax Certificate</option>
            <option value="certificate">Certificate of Occupancy</option>
          </select>
          <input placeholder="Reference Number" value={formData.number || ''} onChange={e => setFormData({...formData, number: e.target.value})} style={inputStyle} />
          <input type="date" placeholder="Issue Date" value={formData.issue_date || ''} onChange={e => setFormData({...formData, issue_date: e.target.value})} style={inputStyle} />
          <input type="date" placeholder="Expiry Date" value={formData.expiry_date || ''} onChange={e => setFormData({...formData, expiry_date: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'active'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="pending">Pending</option>
          </select>
          <textarea placeholder="Notes" rows="3" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
        </>
      )
    }
    
    if (activeTab === 'cases') {
      return (
        <>
          <input placeholder="Case Title" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
          <select value={formData.type || ''} onChange={e => setFormData({...formData, type: e.target.value})} required style={inputStyle}>
            <option value="">Select Case Type</option>
            <option value="dispute">Property Dispute</option>
            <option value="eviction">Eviction</option>
            <option value="contract">Contract Breach</option>
            <option value="title">Title Issue</option>
            <option value="zoning">Zoning Violation</option>
            <option value="other">Other</option>
          </select>
          <textarea placeholder="Case Description" rows="4" value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} style={inputStyle} />
          <select value={formData.client_id || ''} onChange={e => setFormData({...formData, client_id: e.target.value})} style={inputStyle}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <select value={formData.property_id || ''} onChange={e => setFormData({...formData, property_id: e.target.value})} style={inputStyle}>
            <option value="">Select Property</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <input placeholder="Opposing Party" value={formData.opposing_party || ''} onChange={e => setFormData({...formData, opposing_party: e.target.value})} style={inputStyle} />
          <select value={formData.status || 'open'} onChange={e => setFormData({...formData, status: e.target.value})} style={inputStyle}>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="settled">Settled</option>
            <option value="closed">Closed</option>
          </select>
          <select value={formData.priority || 'medium'} onChange={e => setFormData({...formData, priority: e.target.value})} style={inputStyle}>
            <option value="high">High Priority (Urgent)</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
          <input placeholder="Legal Fees" type="number" value={formData.legal_fees || ''} onChange={e => setFormData({...formData, legal_fees: parseFloat(e.target.value)})} style={inputStyle} />
          <select value={formData.currency || 'USD'} onChange={e => setFormData({...formData, currency: e.target.value})} style={inputStyle}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="NGN">NGN</option>
          </select>
          <input type="date" placeholder="Filed Date" value={formData.filed_date || ''} onChange={e => setFormData({...formData, filed_date: e.target.value})} style={inputStyle} />
          <textarea placeholder="Additional Notes" rows="3" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={inputStyle} />
        </>
      )
    }
    return null
  }

  const renderList = () => {
    if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>⏳ Loading legal data...</div>
    
    const cardStyle = { background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }
    const editBtn = { background: '#2c7da0', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, marginRight: 8 }
    const deleteBtn = { background: '#dc2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
    
    // CONTRACTS
    if (activeTab === 'contracts') {
      if (contracts.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No contracts yet. Click "+ Add New" to create one.</p>
      
      const filteredContracts = contracts.filter(c => {
        if (filterStatus === 'all') return true
        return c.status === filterStatus
      })

      return (
        <div>
          <div style={{ marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input 
              type="text" 
              placeholder="🔍 Search contracts..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: 8, flex: 1 }}
            />
            <select 
              value={filterStatus} 
              onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: 8 }}
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="signed">Signed</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div style={responsiveStyles.grid}>
            {filteredContracts.map(c => {
              const client = clients.find(cl => cl.id === c.client_id)
              const property = properties.find(p => p.id === c.property_id)
              const daysLeft = c.end_date ? Math.ceil((new Date(c.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : null
              const isExpiringSoon = daysLeft !== null && daysLeft <= 30 && daysLeft > 0
              const isExpired = c.end_date && new Date(c.end_date) < new Date()
              
              return (
                <div key={c.id} style={{...cardStyle, borderLeft: isExpiringSoon ? '4px solid #f59e0b' : isExpired ? '4px solid #dc2626' : 'none'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                    <h3 style={{margin: 0}}>{c.title}</h3>
                    {isExpiringSoon && <span style={{background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: 12, fontSize: 11}}>⏳ {daysLeft} days</span>}
                    {isExpired && <span style={{background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 12, fontSize: 11}}>❌ Expired</span>}
                  </div>
                  <p style={{margin: '4px 0'}}>#{c.contract_number} | {c.type}</p>
                  <p style={{margin: '4px 0'}}>👤 Client: {client?.full_name || 'N/A'}</p>
                  <p style={{margin: '4px 0'}}>🏠 Property: {property?.title || 'N/A'}</p>
                  <p style={{margin: '4px 0'}}>💰 Value: {c.currency} {c.value?.toLocaleString()}</p>
                  <p style={{margin: '4px 0', fontSize: 13, color: '#64748b'}}>📅 {c.start_date ? new Date(c.start_date).toLocaleDateString() : 'N/A'} - {c.end_date ? new Date(c.end_date).toLocaleDateString() : 'N/A'}</p>
                  <p style={{margin: '4px 0'}}>Status: <span style={{background: c.status === 'signed' ? '#d1fae5' : c.status === 'expired' ? '#fee2e2' : '#fed7aa', padding: '4px 12px', borderRadius: 20, fontSize: 13}}>{c.status}</span></p>
                  <div style={{marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8}}>
                    <button 
                      onClick={() => {
                        setSigningContractId(c.id)
                        setSigningRole('client')
                        setShowSignatureModal(true)
                      }} 
                      style={{...editBtn, background: '#7c3aed'}}
                    >
                      ✍️ Sign
                    </button>
                    {client && (
                      <button 
                        onClick={() => {
                          setSelectedClient(client)
                          setEmailCompose({ 
                            to: client.email, 
                            subject: `Regarding Contract: ${c.title}`,
                            body: `Dear ${client.full_name},\n\nI am writing regarding your contract "${c.title}" (${c.contract_number}).\n\nBest regards,\n${companyName} Legal Team`,
                            client_id: client.id 
                          })
                          setShowEmailModal(true)
                        }} 
                        style={{...editBtn, background: '#059669'}}
                      >
                        📧 Email Client
                      </button>
                    )}
                    <button onClick={() => sendExpiryAlert(c.id)} style={{...editBtn, background: '#f59e0b'}}>📨 Send Alert</button>
                    <button onClick={() => { setEditingItem(c); setFormData(c); setShowModal(true) }} style={editBtn}>Edit</button>
                    <button onClick={() => handleDelete(c.id)} style={deleteBtn}>Delete</button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    }
    
    // DOCUMENTS
    if (activeTab === 'documents') {
      if (documents.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No documents yet. Click "Upload Document" to add one.</p>
      return (
        <div>
          <div style={{ marginBottom: 20 }}>
            <input 
              type="text" 
              placeholder="🔍 Search documents..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: 8, width: '100%' }}
            />
          </div>
          <div style={responsiveStyles.grid}>
            {documents.map(d => {
              const isImage = d.file_url?.match(/\.(jpg|jpeg|png|gif)$/i)
              return (
                <div key={d.id} style={cardStyle}>
                  <h3>📎 {d.title}</h3>
                  <p style={{margin: '4px 0'}}>Type: {d.type || 'General'} | Version: v{d.version || 1}</p>
                  <p style={{margin: '4px 0', fontSize: 13, color: '#64748b'}}>Uploaded: {new Date(d.created_at).toLocaleDateString()}</p>
                  <div style={{display: 'flex', gap: 8, marginTop: 15, flexWrap: 'wrap'}}>
                    {d.file_url ? (
                      <a href={d.file_url} target="_blank" rel="noopener noreferrer" style={{...editBtn, background: '#059669', textDecoration: 'none'}}>
                        {isImage ? '🖼️ View Image' : '📥 Download'}
                      </a>
                    ) : <span style={{color: '#999'}}>No file</span>}
                    <button onClick={() => deleteDocument(d.id)} style={deleteBtn}>Delete</button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    }
    
    // COMPLIANCE
    if (activeTab === 'compliance') {
      if (compliance.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No compliance records yet. Click "+ Add New" to create one.</p>
      return (
        <div style={responsiveStyles.grid}>
          {compliance.map(c => {
            const property = properties.find(p => p.id === c.property_id)
            const isExpired = c.expiry_date && new Date(c.expiry_date) < new Date()
            const daysLeft = c.expiry_date ? Math.ceil((new Date(c.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)) : null
            return (
              <div key={c.id} style={{...cardStyle, borderLeft: isExpired ? '4px solid #dc2626' : daysLeft <= 30 ? '4px solid #f59e0b' : 'none'}}>
                <h3 style={{margin: 0}}>{c.type}</h3>
                <p style={{margin: '4px 0'}}>🏠 Property: {property?.title || 'N/A'}</p>
                <p style={{margin: '4px 0'}}>📄 Number: {c.number || 'N/A'}</p>
                <p style={{margin: '4px 0', fontSize: 13, color: '#64748b'}}>📅 Issued: {c.issue_date ? new Date(c.issue_date).toLocaleDateString() : 'N/A'}</p>
                <p style={{margin: '4px 0', fontSize: 13, color: '#64748b'}}>⚠️ Expires: {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : 'N/A'}</p>
                {!isExpired && daysLeft <= 30 && daysLeft > 0 && <p style={{color: '#f59e0b', margin: '4px 0'}}>⚠️ Expires in {daysLeft} days</p>}
                {isExpired && <p style={{color: '#dc2626', margin: '4px 0'}}>❌ EXPIRED - Renewal required</p>}
                <p style={{margin: '4px 0'}}>Status: <span style={{background: c.status === 'active' ? '#d1fae5' : '#fee2e2', padding: '4px 12px', borderRadius: 20}}>{c.status}</span></p>
                <div style={{marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8}}>
                  <button onClick={() => { setEditingItem(c); setFormData(c); setShowModal(true) }} style={editBtn}>Edit</button>
                  <button onClick={() => handleDelete(c.id)} style={deleteBtn}>Delete</button>
                </div>
              </div>
            )
          })}
        </div>
      )
    }
    
    // LEGAL CASES
    if (activeTab === 'cases') {
      if (legalCases.length === 0) return <p style={{ textAlign: 'center', padding: 40 }}>No legal cases yet. Click "+ Add New" to create one.</p>
      return (
        <div style={responsiveStyles.grid}>
          {legalCases.map(c => {
            const client = clients.find(cl => cl.id === c.client_id)
            const property = properties.find(p => p.id === c.property_id)
            return (
              <div key={c.id} style={cardStyle}>
                <h3 style={{margin: '0 0 8px 0'}}>⚖️ {c.title}</h3>
                <p style={{margin: '2px 0'}}>#{c.case_number} | {c.type}</p>
                <p style={{margin: '2px 0'}}>👤 Client: {client?.full_name || 'N/A'}</p>
                <p style={{margin: '2px 0'}}>🏠 Property: {property?.title || 'N/A'}</p>
                <p style={{margin: '2px 0'}}>⚔️ Opposing: {c.opposing_party || 'N/A'}</p>
                <p style={{margin: '2px 0'}}>Status: <span style={{background: c.status === 'won' ? '#d1fae5' : c.status === 'lost' ? '#fee2e2' : '#fed7aa', padding: '4px 12px', borderRadius: 20}}>{c.status}</span></p>
                <p style={{margin: '2px 0'}}>Priority: <span style={{background: c.priority === 'high' ? '#fee2e2' : '#fef3c7', padding: '4px 12px', borderRadius: 20}}>{c.priority}</span></p>
                <p style={{margin: '2px 0'}}>💰 Legal Fees: {c.currency} {c.legal_fees?.toLocaleString()}</p>
                <p style={{margin: '2px 0', fontSize: 13, color: '#64748b'}}>📅 Filed: {c.filed_date ? new Date(c.filed_date).toLocaleDateString() : 'N/A'}</p>
                <div style={{marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8}}>
                  <button onClick={() => { setEditingItem(c); setFormData(c); setShowModal(true) }} style={editBtn}>Edit</button>
                  <button onClick={() => handleDelete(c.id)} style={deleteBtn}>Delete</button>
                  <WhatsAppButton type="contract" data={{ title: c.title, contract_number: c.case_number }} phoneNumber="" session={session} companyName={companyName} />
                </div>
              </div>
            )
          })}
        </div>
      )
    }
    return null
  }

  // Styles
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 15 }
  const statCard = { background: '#fff', padding: 20, borderRadius: 16, textAlign: 'center', border: '1px solid #e2e8f0' }
  const statNumber = { fontSize: 36, fontWeight: 'bold', color: '#1e3a5f', marginTop: 10 }
  const recentCard = { background: '#fff', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0', marginBottom: 30 }
  const recentItem = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #e2e8f0', gap: 10, flexWrap: 'wrap' }
  const inputStyle = { width: '100%', padding: 10, margin: '6px 0', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' }
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
  const modalStyle = { background: '#fff', padding: 25, borderRadius: 20, width: '90%', maxWidth: 550, maxHeight: '80vh', overflow: 'auto' }
  const saveBtn = { background: '#059669', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }
  const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 10, cursor: 'pointer' }
  const addBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 25, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
  const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }

  if (loading && !companyId) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading company data...</div>
  }

  return (
    <PortalLayout
      session={session}
      onLogout={onLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      unreadCount={unreadCount}
      portalTitle="Legal Portal"
      portalIcon="⚖️"
      onShowMessages={() => setShowMessages(true)}
    >
      {/* Messages Modal */}
      {showMessages && (
        <div style={modalOverlayStyle} onClick={() => setShowMessages(false)}>
          <div onClick={e => e.stopPropagation()}>
            <MessagingSystem 
              currentUser={session.user}
              userRole={userRole}
              onClose={() => setShowMessages(false)}
            />
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div style={modalOverlay} onClick={() => setShowEmailModal(false)}>
          <div style={{...modalStyle, maxWidth: 500}} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>📧 {selectedClient ? `Send Email to ${selectedClient.full_name}` : 'Compose Email'}</h2>
            <input 
              placeholder="To" 
              value={emailCompose.to} 
              onChange={e => setEmailCompose({...emailCompose, to: e.target.value})} 
              style={inputStyle} 
            />
            <input 
              placeholder="Subject" 
              value={emailCompose.subject} 
              onChange={e => setEmailCompose({...emailCompose, subject: e.target.value})} 
              style={inputStyle} 
            />
            <textarea 
              placeholder="Message" 
              rows="8" 
              value={emailCompose.body} 
              onChange={e => setEmailCompose({...emailCompose, body: e.target.value})} 
              style={inputStyle} 
            />
            <div style={{display: 'flex', gap: 10, marginTop: 20}}>
              <button onClick={sendEmail} style={saveBtn}>Send Email</button>
              <button onClick={() => setShowEmailModal(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>{activeTab === 'dashboard' ? 'Legal Dashboard' : activeTab === 'whatsapp' ? 'WhatsApp Messaging' : activeTab === 'emails' ? 'Email Management' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Welcome, {session.user.email} - {companyName} Legal</p>
        </div>
        {activeTab !== 'dashboard' && activeTab !== 'documents' && activeTab !== 'whatsapp' && activeTab !== 'emails' && (
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>
            + Add New
          </button>
        )}
        {activeTab === 'documents' && (
          <button onClick={() => setShowDocModal(true)} style={addBtn}>📎 Upload Document</button>
        )}
      </div>

      {errorMsg && (
        <div style={{background: '#fee2e2', color: '#dc2626', padding: 10, borderRadius: 8, marginBottom: 20}}>
          ⚠️ {errorMsg}
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          <div style={responsiveStyles.statsGrid}>
            <div style={statCard}><h3 style={{ fontSize: '14px', color: '#64748b' }}>📄 Contracts</h3><p style={statNumber}>{stats.totalContracts}</p><small>Total</small></div>
            <div style={statCard}><h3 style={{ fontSize: '14px', color: '#64748b' }}>✅ Signed</h3><p style={statNumber}>{stats.signedContracts}</p><small>Fully signed</small></div>
            <div style={statCard}><h3 style={{ fontSize: '14px', color: '#64748b' }}>⚠️ Expiring</h3><p style={statNumber}>{stats.expiringSoon}</p><small>Next 30 days</small></div>
            <div style={statCard}><h3 style={{ fontSize: '14px', color: '#64748b' }}>📎 Documents</h3><p style={statNumber}>{stats.totalDocuments}</p><small>Uploaded</small></div>
            <div style={statCard}><h3 style={{ fontSize: '14px', color: '#64748b' }}>⚖️ Cases</h3><p style={statNumber}>{stats.totalCases}</p><small>{stats.openCases} open</small></div>
          </div>
          
          {expiringContracts.length > 0 && (
            <div style={recentCard}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f', margin: '0 0 16px 0' }}>⚠️ Contracts Expiring Soon ({expiringContracts.length})</h3>
              {expiringContracts.slice(0, 5).map(c => {
                const daysLeft = Math.ceil((new Date(c.end_date) - new Date()) / (1000 * 60 * 60 * 24))
                const client = clients.find(cl => cl.id === c.client_id)
                return (
                  <div key={c.id} style={recentItem}>
                    <span><strong>{c.title}</strong> - {client?.full_name || 'N/A'}</span>
                    <span style={{color: daysLeft <= 7 ? '#dc2626' : '#f59e0b'}}>{daysLeft} days left</span>
                    <button onClick={() => sendExpiryAlert(c.id)} style={{background: '#f59e0b', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12}}>📨 Alert</button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div style={{ marginTop: 20 }}>
        {['contracts', 'documents', 'compliance', 'cases'].includes(activeTab) && renderList()}
      </div>

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div>
          <h2 style={{ fontSize: '24px', color: '#1e3a5f' }}>📱 WhatsApp Messaging - {companyName}</h2>
          <div style={responsiveStyles.grid}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>📄 Share Contract</h3>
              <p>Send contract details to clients via WhatsApp</p>
              {contracts.filter(c => c.status !== 'signed').slice(0, 5).map(contract => {
                const client = clients.find(c => c.id === contract.client_id)
                return (
                  <div key={contract.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{contract.title}</strong> - {contract.contract_number}</p>
                    <p>Client: {client?.full_name || 'N/A'}</p>
                    <WhatsAppButton type="contract" data={contract} phoneNumber={client?.phone} session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>✅ Share Compliance Document</h3>
              <p>Send compliance documents via WhatsApp</p>
              {compliance.filter(c => c.status === 'active').slice(0, 5).map(comp => {
                const property = properties.find(p => p.id === comp.property_id)
                return (
                  <div key={comp.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{comp.type}</strong> - {comp.number || 'N/A'}</p>
                    <p>Property: {property?.title || 'N/A'}</p>
                    <WhatsAppButton type="contract" data={{ title: comp.type, contract_number: comp.number }} phoneNumber="" session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>⚖️ Share Case Update</h3>
              <p>Send legal case updates to clients</p>
              {legalCases.filter(c => c.status === 'open' || c.status === 'pending').slice(0, 5).map(caseItem => {
                const client = clients.find(c => c.id === caseItem.client_id)
                return (
                  <div key={caseItem.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{caseItem.title}</strong> - {caseItem.case_number}</p>
                    <p>Client: {client?.full_name || 'N/A'}</p>
                    <WhatsAppButton type="contract" data={{ title: caseItem.title, contract_number: caseItem.case_number }} phoneNumber={client?.phone} session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Emails Tab */}
      {activeTab === 'emails' && (
        <div>
          <h2 style={{ fontSize: '24px', color: '#1e3a5f' }}>📧 Email Management - {companyName}</h2>
          <div style={responsiveStyles.grid}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>✏️ Compose New Email</h3>
              <p>Send emails to clients or other contacts</p>
              <button 
                onClick={() => {
                  setSelectedClient(null)
                  setEmailCompose({ to: '', subject: '', body: '', client_id: null })
                  setShowEmailModal(true)
                }} 
                style={addBtn}
              >
                📧 Compose Email
              </button>
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>👥 Quick Email to Clients</h3>
              <p>Send emails to your clients</p>
              {clients.slice(0, 5).map(client => (
                <div key={client.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                  <p><strong>{client.full_name}</strong> - {client.email}</p>
                  <button 
                    onClick={() => {
                      setSelectedClient(client)
                      setEmailCompose({ 
                        to: client.email, 
                        subject: '', 
                        body: '', 
                        client_id: client.id 
                      })
                      setShowEmailModal(true)
                    }} 
                    style={{background: '#059669', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13}}
                  >
                    📧 Send Email
                  </button>
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>📝 Email Templates</h3>
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Contract Ready for Signature - ${companyName}`,
                      body: `Dear Client,\n\nYour contract is now ready for review and signature.\n\nBest regards,\n${companyName} Legal Team`,
                      client_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{background: '#1e3a5f', color: '#fff', border: 'none', padding: '8px', borderRadius: 6, cursor: 'pointer', textAlign: 'left', width: '100%', marginBottom: 8}}
                >
                  📝 Contract Ready Template
                </button>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Contract Expiry Notice - ${companyName}`,
                      body: `Dear Client,\n\nThis is a reminder that your contract is expiring soon.\n\nPlease contact us to discuss renewal.\n\nBest regards,\n${companyName} Legal Team`,
                      client_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{background: '#f59e0b', color: '#fff', border: 'none', padding: '8px', borderRadius: 6, cursor: 'pointer', textAlign: 'left', width: '100%', marginBottom: 8}}
                >
                  📝 Contract Expiry Template
                </button>
                <button 
                  onClick={() => {
                    setEmailCompose({
                      to: '',
                      subject: `Case Update - ${companyName}`,
                      body: `Dear Client,\n\nThere has been an update regarding your legal case.\n\nPlease contact our office for more details.\n\nBest regards,\n${companyName} Legal Team`,
                      client_id: null
                    })
                    setShowEmailModal(true)
                  }}
                  style={{background: '#7c3aed', color: '#fff', border: 'none', padding: '8px', borderRadius: 6, cursor: 'pointer', textAlign: 'left', width: '100%', marginBottom: 8}}
                >
                  ⚖️ Case Update Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>{editingItem ? '✏️ Edit' : '➕ Add New'} {activeTab.slice(0, -1)}</h2>
            <form onSubmit={handleSubmit}>
              {renderForm()}
              <div style={{display: 'flex', gap: 10, marginTop: 20}}>
                <button type="submit" style={saveBtn}>Save</button>
                <button type="button" onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showDocModal && (
        <div style={modalOverlay} onClick={() => setShowDocModal(false)}>
          <div style={{...modalStyle, maxWidth: 400}} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>📎 Upload Document</h2>
            <select value={docType} onChange={e => setDocType(e.target.value)} style={inputStyle}>
              <option value="contract">Contract</option>
              <option value="disclosure">Disclosure</option>
              <option value="compliance">Compliance</option>
              <option value="policy">Policy</option>
              <option value="correspondence">Correspondence</option>
              <option value="other">Other</option>
            </select>
            <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={(e) => {
              if (e.target.files[0]) handleDocumentUpload(e.target.files[0], docType)
            }} style={inputStyle} />
            {uploading && <p style={{textAlign: 'center', marginTop: 10}}>⏳ Uploading...</p>}
            <button onClick={() => setShowDocModal(false)} style={cancelBtn}>Cancel</button>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <div style={modalOverlay} onClick={() => setShowSignatureModal(false)}>
          <div style={{...modalStyle, maxWidth: 500}} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>✍️ Digital Signature</h2>
            <p style={{ marginBottom: 10 }}>You are signing as: <strong>{signingRole === 'client' ? 'Client' : 'Agent'}</strong></p>
            <div style={{border: '2px solid #cbd5e1', borderRadius: 8, padding: 20, margin: '15px 0', textAlign: 'center'}}>
              <canvas ref={canvasRef} width="400" height="150" style={{border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'crosshair', width: '100%', height: 'auto'}}></canvas>
              <div style={{marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center'}}>
                <button type="button" onClick={clearSignature} style={{background: '#6b7280', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: 6, cursor: 'pointer'}}>Clear Signature</button>
              </div>
            </div>
            <div style={{display: 'flex', gap: 10, marginTop: 10}}>
              <button onClick={addSignature} style={saveBtn}>Confirm Signature</button>
              <button onClick={() => setShowSignatureModal(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && selectedTemplate && (
        <div style={modalOverlay} onClick={() => setShowTemplateModal(false)}>
          <div style={{...modalStyle, maxWidth: 500}} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>📄 Generate from Template: {selectedTemplate.name}</h2>
            <div style={{marginTop: 15}}>
              <input placeholder="Property Address" value={templateVars.property_address || ''} onChange={e => setTemplateVars({...templateVars, property_address: e.target.value})} style={inputStyle} />
              <input placeholder="Price / Amount" type="number" value={templateVars.price || ''} onChange={e => setTemplateVars({...templateVars, price: e.target.value})} style={inputStyle} />
              <select value={templateVars.currency || 'USD'} onChange={e => setTemplateVars({...templateVars, currency: e.target.value})} style={inputStyle}>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="NGN">NGN</option>
              </select>
              {selectedTemplate.type === 'sales' && (
                <>
                  <input placeholder="Seller Name" value={templateVars.seller_name || ''} onChange={e => setTemplateVars({...templateVars, seller_name: e.target.value})} style={inputStyle} />
                  <input placeholder="Buyer Name" value={templateVars.buyer_name || ''} onChange={e => setTemplateVars({...templateVars, buyer_name: e.target.value})} style={inputStyle} />
                </>
              )}
              {selectedTemplate.type === 'rental' && (
                <>
                  <input placeholder="Landlord Name" value={templateVars.landlord_name || ''} onChange={e => setTemplateVars({...templateVars, landlord_name: e.target.value})} style={inputStyle} />
                  <input placeholder="Tenant Name" value={templateVars.tenant_name || ''} onChange={e => setTemplateVars({...templateVars, tenant_name: e.target.value})} style={inputStyle} />
                  <input placeholder="Monthly Rent" type="number" value={templateVars.rent || ''} onChange={e => setTemplateVars({...templateVars, rent: e.target.value})} style={inputStyle} />
                </>
              )}
              {selectedTemplate.type === 'nda' && (
                <>
                  <input placeholder="Disclosing Party" value={templateVars.disclosing_party || ''} onChange={e => setTemplateVars({...templateVars, disclosing_party: e.target.value})} style={inputStyle} />
                  <input placeholder="Receiving Party" value={templateVars.receiving_party || ''} onChange={e => setTemplateVars({...templateVars, receiving_party: e.target.value})} style={inputStyle} />
                  <input placeholder="Duration (months)" type="number" value={templateVars.duration_months || ''} onChange={e => setTemplateVars({...templateVars, duration_months: e.target.value})} style={inputStyle} />
                </>
              )}
              {selectedTemplate.type === 'listing' && (
                <>
                  <input placeholder="Broker Name" value={templateVars.broker_name || ''} onChange={e => setTemplateVars({...templateVars, broker_name: e.target.value})} style={inputStyle} />
                  <input placeholder="List Price" type="number" value={templateVars.list_price || ''} onChange={e => setTemplateVars({...templateVars, list_price: e.target.value})} style={inputStyle} />
                  <input placeholder="Commission %" type="number" value={templateVars.commission || ''} onChange={e => setTemplateVars({...templateVars, commission: e.target.value})} style={inputStyle} />
                </>
              )}
              {selectedTemplate.type === 'management' && (
                <>
                  <input placeholder="Owner Name" value={templateVars.owner_name || ''} onChange={e => setTemplateVars({...templateVars, owner_name: e.target.value})} style={inputStyle} />
                  <input placeholder="Manager Name" value={templateVars.manager_name || ''} onChange={e => setTemplateVars({...templateVars, manager_name: e.target.value})} style={inputStyle} />
                  <input placeholder="Management Fee %" type="number" value={templateVars.management_fee || ''} onChange={e => setTemplateVars({...templateVars, management_fee: e.target.value})} style={inputStyle} />
                </>
              )}
            </div>
            <div style={{display: 'flex', gap: 10, marginTop: 20}}>
              <button onClick={insertFromTemplate} style={saveBtn}>Generate Contract</button>
              <button onClick={() => setShowTemplateModal(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  )
}

export default LegalPortal